import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface Trip {
  id: string;
  name: string;
  destination: string;
  start_date: string;
  end_date: string;
  status: string;
  notes: string;
  summary: string;
  item_count: number;
  doc_count: number;
  completed_count?: number;
}

interface ItineraryItem {
  id: string;
  item_date: string;
  item_time: string;
  item_type: string;
  title: string;
  description: string;
  location: string;
  address: string;
  booking_reference: string;
  contact_info: string;
  notes: string;
  source: string;
  is_completed?: boolean;
}

interface TripDocument {
  id: string;
  document_type: string;
  file_name: string;
  is_processed: boolean;
  created_at: string;
}

const TravelPlanner: React.FC = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [itinerary, setItinerary] = useState<ItineraryItem[]>([]);
  const [documents, setDocuments] = useState<TripDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  
  // Selection state for bulk operations
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  // Form states
  const [newTrip, setNewTrip] = useState({ name: '', destination: '', startDate: '', endDate: '', notes: '' });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('flight_ticket');
  const [newItem, setNewItem] = useState({
    itemDate: '', itemTime: '', itemType: 'activity', title: '', description: '',
    location: '', address: '', bookingReference: '', contactInfo: '', notes: ''
  });

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getTrips();
      if (response.success) {
        setTrips(response.trips || []);
      }
    } catch (error) {
      console.error('Failed to fetch trips:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTripDetails = useCallback(async (tripId: string) => {
    try {
      const response = await apiService.getTripDetails(tripId);
      if (response.success) {
        setSelectedTrip(response.trip);
        setItinerary(response.itinerary || []);
        setDocuments(response.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch trip details:', error);
    }
  }, []);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  const handleCreateTrip = async () => {
    if (!newTrip.name || !newTrip.startDate || !newTrip.endDate) return;
    try {
      const response = await apiService.createTrip(newTrip);
      if (response.success) {
        setTrips([response.trip, ...trips]);
        setShowCreateModal(false);
        setNewTrip({ name: '', destination: '', startDate: '', endDate: '', notes: '' });
        fetchTripDetails(response.trip.id);
      }
    } catch (error) {
      console.error('Failed to create trip:', error);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedTrip) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('documentType', uploadType);

      const response = await apiService.uploadTripDocument(selectedTrip.id, formData);
      if (response.success) {
        setShowUploadModal(false);
        setUploadFile(null);
        fetchTripDetails(selectedTrip.id);
        if (response.itineraryItemsCreated > 0) {
          alert(`✅ Document processed! ${response.itineraryItemsCreated} itinerary items extracted.`);
        } else if (response.extractionError) {
          alert(`⚠️ Document uploaded but AI extraction failed: ${response.extractionError}\n\nYou can still add items manually.`);
        } else {
          alert('📄 Document uploaded. No itinerary items could be extracted - you can add them manually.');
        }
      }
    } catch (error: any) {
      console.error('Failed to upload document:', error);
      alert(error.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.title || !newItem.itemDate || !selectedTrip) return;
    try {
      const response = await apiService.addTripItineraryItem(selectedTrip.id, newItem);
      if (response.success) {
        setItinerary([...itinerary, response.item].sort((a, b) => 
          new Date(a.item_date + ' ' + (a.item_time || '00:00')).getTime() - 
          new Date(b.item_date + ' ' + (b.item_time || '00:00')).getTime()
        ));
        setShowAddItemModal(false);
        setNewItem({
          itemDate: '', itemTime: '', itemType: 'activity', title: '', description: '',
          location: '', address: '', bookingReference: '', contactInfo: '', notes: ''
        });
      }
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedTrip) return;
    setGeneratingSummary(true);
    try {
      const response = await apiService.generateTripSummary(selectedTrip.id);
      if (response.success) {
        setSelectedTrip({ ...selectedTrip, summary: response.summary });
      }
    } catch (error) {
      console.error('Failed to generate summary:', error);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!selectedTrip || !confirm('Delete this item?')) return;
    try {
      await apiService.deleteTripItineraryItem(selectedTrip.id, itemId);
      setItinerary(itinerary.filter(i => i.id !== itemId));
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!selectedTrip || !confirm('Delete this document? Any itinerary items extracted from it will also be deleted.')) return;
    try {
      await apiService.deleteTripDocument(selectedTrip.id, documentId);
      fetchTripDetails(selectedTrip.id);
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert('Failed to delete document');
    }
  };

  const handleDownloadDocument = async (documentId: string, fileName: string) => {
    if (!selectedTrip) return;
    try {
      const blob = await apiService.downloadTripDocument(selectedTrip.id, documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Failed to download document:', error);
      alert(error.response?.data?.error || 'Failed to download document');
    }
  };

  const toggleItemCompletion = async (itemId: string) => {
    if (!selectedTrip) return;
    const item = itinerary.find(i => i.id === itemId);
    if (!item) return;
    
    try {
      await apiService.updateTripItineraryItem(selectedTrip.id, itemId, {
        isCompleted: !item.is_completed
      });
      setItinerary(itinerary.map(i => 
        i.id === itemId ? { ...i, is_completed: !i.is_completed } : i
      ));
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  // Calculate trip completion percentage
  const getTripCompletion = () => {
    if (itinerary.length === 0) return 0;
    const completed = itinerary.filter(i => i.is_completed).length;
    return Math.round((completed / itinerary.length) * 100);
  };

  const isTripPast = () => {
    if (!selectedTrip) return false;
    return new Date(selectedTrip.end_date) < new Date();
  };

  // Selection handlers
  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const selectAllItems = () => {
    if (selectedItems.size === itinerary.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(itinerary.map(i => i.id)));
    }
  };

  const deleteSelectedItems = async () => {
    if (!selectedTrip || selectedItems.size === 0) return;
    if (!confirm(`Delete ${selectedItems.size} selected items?`)) return;
    
    try {
      for (const itemId of selectedItems) {
        await apiService.deleteTripItineraryItem(selectedTrip.id, itemId);
      }
      setItinerary(itinerary.filter(i => !selectedItems.has(i.id)));
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Failed to delete items:', error);
      alert('Failed to delete some items');
      fetchTripDetails(selectedTrip.id);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    if (!draggedItemId || draggedItemId === targetItemId) return;
    
    const draggedIndex = itinerary.findIndex(i => i.id === draggedItemId);
    const targetIndex = itinerary.findIndex(i => i.id === targetItemId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    const newItinerary = [...itinerary];
    const [draggedItem] = newItinerary.splice(draggedIndex, 1);
    newItinerary.splice(targetIndex, 0, draggedItem);
    
    setItinerary(newItinerary);
    setDraggedItemId(null);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    return `${h > 12 ? h - 12 : h}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const getItemTypeIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      flight: '✈️', hotel_checkin: '🏨', hotel_checkout: '🚪', activity: '🎯',
      meal: '🍽️', transport: '🚗', meeting: '👥', other: '📌'
    };
    return icons[type] || '📌';
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      upcoming: '#3b82f6', ongoing: '#22c55e', completed: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  // Group itinerary by date
  const groupedItinerary = itinerary.reduce((acc, item) => {
    const date = item.item_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {} as { [key: string]: ItineraryItem[] });

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading travel plans...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2rem' }}>✈️</span> Travel Planner
          </h1>
          <p style={{ color: '#6b7280', marginTop: '4px' }}>Plan trips, upload tickets & itineraries, get AI-extracted schedules</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="create-trip-btn"
          style={{
            padding: '12px 24px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          + New Trip
        </button>
      </div>

      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Trip List */}
        <div style={{ width: '320px', flexShrink: 0 }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280', marginBottom: '12px' }}>Your Trips</h3>
          {trips.length === 0 ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              border: '2px dashed #e5e7eb'
            }}>
              <p style={{ fontSize: '3rem', marginBottom: '12px' }}>🌍</p>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>No trips planned yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Plan Your First Trip
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {trips.map(trip => {
                const completionPercent = trip.item_count > 0 
                  ? Math.round(((trip.completed_count || 0) / trip.item_count) * 100) 
                  : 0;
                const isPast = new Date(trip.end_date) < new Date();
                
                return (
                <div
                  key={trip.id}
                  onClick={() => fetchTripDetails(trip.id)}
                  style={{
                    padding: '16px',
                    backgroundColor: selectedTrip?.id === trip.id ? '#f0f4ff' : 'white',
                    border: `2px solid ${selectedTrip?.id === trip.id ? '#667eea' : '#e5e7eb'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>{trip.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {trip.destination && <span>{trip.destination} · </span>}
                        {formatDate(trip.start_date)} - {formatDate(trip.end_date)}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: getStatusColor(trip.status) + '20',
                      color: getStatusColor(trip.status),
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'capitalize'
                    }}>
                      {trip.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '0.75rem', color: '#9ca3af', alignItems: 'center' }}>
                    <span>📋 {trip.item_count} items</span>
                    <span>📄 {trip.doc_count} docs</span>
                    {trip.item_count > 0 && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: completionPercent === 100 ? '#dcfce7' : (isPast ? '#fef3c7' : '#f3f4f6'),
                        color: completionPercent === 100 ? '#166534' : (isPast ? '#92400e' : '#6b7280'),
                        borderRadius: '10px',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}>
                        {completionPercent}%
                      </span>
                    )}
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>

        {/* Trip Details */}
        {selectedTrip ? (
          <div style={{ flex: 1 }}>
            {/* Trip Header */}
            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px',
              color: 'white',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{selectedTrip.name}</h2>
                  {selectedTrip.destination && (
                    <p style={{ opacity: 0.9, marginTop: '4px' }}>📍 {selectedTrip.destination}</p>
                  )}
                  <p style={{ opacity: 0.8, marginTop: '8px' }}>
                    {formatDate(selectedTrip.start_date)} → {formatDate(selectedTrip.end_date)}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    📄 Upload Document
                  </button>
                  <button
                    onClick={() => {
                      setNewItem({ ...newItem, itemDate: selectedTrip.start_date });
                      setShowAddItemModal(true);
                    }}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    + Add Activity
                  </button>
                </div>
              </div>
            </div>

            {/* Uploaded Documents */}
            {documents.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                marginBottom: '20px'
              }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '12px' }}>Uploaded Documents</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {documents.map(doc => (
                    <div key={doc.id} style={{
                      padding: '8px 12px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{doc.is_processed ? '✅' : '⏳'}</span>
                      <span title={doc.file_name}>{doc.file_name.length > 20 ? doc.file_name.substring(0, 20) + '...' : doc.file_name}</span>
                      <button
                        onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                        title="Download document"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#667eea',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          fontSize: '0.8rem',
                          fontWeight: 600
                        }}
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        title="Delete document"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          fontSize: '0.9rem',
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Calendar View / Itinerary */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>📅 Itinerary</h3>
                  <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{itinerary.length} items</span>
                  {itinerary.length > 0 && (
                    <span style={{
                      padding: '4px 10px',
                      backgroundColor: getTripCompletion() === 100 ? '#dcfce7' : (isTripPast() ? '#fef3c7' : '#f3f4f6'),
                      color: getTripCompletion() === 100 ? '#166534' : (isTripPast() ? '#92400e' : '#6b7280'),
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      {getTripCompletion()}% complete
                    </span>
                  )}
                </div>
                {itinerary.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.size === itinerary.length && itinerary.length > 0}
                        onChange={selectAllItems}
                        style={{ width: '16px', height: '16px' }}
                      />
                      Select All
                    </label>
                    {selectedItems.size > 0 && (
                      <button
                        onClick={deleteSelectedItems}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Delete {selectedItems.size} Selected
                      </button>
                    )}
                  </div>
                )}
              </div>

              {Object.keys(groupedItinerary).length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  <p style={{ fontSize: '2rem', marginBottom: '8px' }}>📋</p>
                  <p>No itinerary items yet</p>
                  <p style={{ fontSize: '0.875rem' }}>Upload a ticket/booking PDF or add items manually</p>
                </div>
              ) : (
                <div style={{ padding: '16px' }}>
                  {Object.entries(groupedItinerary).sort(([a], [b]) => a.localeCompare(b)).map(([date, items]) => (
                    <div key={date} style={{ marginBottom: '24px' }}>
                      {/* Date Header */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          backgroundColor: '#667eea',
                          borderRadius: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}>
                          <span style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>
                            {new Date(date).toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {new Date(date).getDate()}
                          </span>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {new Date(date).toLocaleDateString('en-US', { weekday: 'long' })}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            {items.length} {items.length === 1 ? 'activity' : 'activities'}
                          </div>
                        </div>
                      </div>

                      {/* Items for this date */}
                      <div style={{ marginLeft: '62px', borderLeft: '2px solid #e5e7eb', paddingLeft: '20px' }}>
                        {items.sort((a, b) => (a.item_time || '').localeCompare(b.item_time || '')).map(item => (
                          <div
                            key={item.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, item.id)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, item.id)}
                            onDragEnd={handleDragEnd}
                            style={{
                              padding: '16px',
                              backgroundColor: draggedItemId === item.id ? '#e0e7ff' : (selectedItems.has(item.id) ? '#f0f4ff' : '#f9fafb'),
                              borderRadius: '12px',
                              marginBottom: '12px',
                              position: 'relative',
                              border: selectedItems.has(item.id) ? '2px solid #667eea' : '2px solid transparent',
                              cursor: 'grab',
                              transition: 'all 0.2s'
                            }}
                          >
                            {/* Completion checkbox */}
                            <input
                              type="checkbox"
                              checked={item.is_completed || false}
                              onChange={() => toggleItemCompletion(item.id)}
                              title="Mark as complete"
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '70px',
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer',
                                accentColor: '#22c55e'
                              }}
                            />
                            
                            {/* Selection checkbox */}
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '40px',
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer'
                              }}
                            />
                            
                            {/* Drag handle */}
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              color: '#9ca3af',
                              fontSize: '1rem',
                              cursor: 'grab'
                            }} title="Drag to reorder">
                              ⋮⋮
                            </div>

                            <div style={{
                              position: 'absolute',
                              left: '-31px',
                              top: '20px',
                              width: '20px',
                              height: '20px',
                              backgroundColor: item.is_completed ? '#22c55e' : 'white',
                              border: `2px solid ${item.is_completed ? '#22c55e' : '#667eea'}`,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              color: item.is_completed ? 'white' : 'inherit'
                            }}>
                              {item.is_completed ? '✓' : getItemTypeIcon(item.item_type)}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingRight: '90px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  {item.item_time && (
                                    <span style={{
                                      padding: '2px 8px',
                                      backgroundColor: item.is_completed ? '#86efac' : '#667eea',
                                      color: item.is_completed ? '#166534' : 'white',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      fontWeight: 600
                                    }}>
                                      {formatTime(item.item_time)}
                                    </span>
                                  )}
                                  <span style={{ 
                                    fontWeight: 600,
                                    textDecoration: item.is_completed ? 'line-through' : 'none',
                                    color: item.is_completed ? '#9ca3af' : 'inherit'
                                  }}>{item.title}</span>
                                  {item.source === 'ai_extracted' && (
                                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>🤖 AI</span>
                                  )}
                                </div>
                                {item.description && (
                                  <p style={{ fontSize: '0.85rem', color: '#4b5563', margin: '4px 0' }}>{item.description}</p>
                                )}
                                {item.location && (
                                  <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '4px 0' }}>📍 {item.location}</p>
                                )}
                                {item.address && (
                                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '2px 0' }}>{item.address}</p>
                                )}
                                {item.booking_reference && (
                                  <p style={{ fontSize: '0.75rem', color: '#667eea', margin: '4px 0' }}>
                                    Ref: {item.booking_reference}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '4px'
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trip Summary */}
            {selectedTrip.status === 'completed' && (
              <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f0fdf4',
                borderRadius: '12px',
                border: '1px solid #86efac'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0 }}>✨ Trip Summary</h4>
                  <button
                    onClick={handleGenerateSummary}
                    disabled={generatingSummary}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#22c55e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      cursor: generatingSummary ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {generatingSummary ? 'Generating...' : 'Generate Summary'}
                  </button>
                </div>
                {selectedTrip.summary ? (
                  <p style={{ fontSize: '0.9rem', lineHeight: 1.6, color: '#166534' }}>{selectedTrip.summary}</p>
                ) : (
                  <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Click &quot;Generate Summary&quot; to create an AI-powered recap of your trip!
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f9fafb',
            borderRadius: '16px',
            minHeight: '500px'
          }}>
            <div style={{ textAlign: 'center', color: '#6b7280' }}>
              <p style={{ fontSize: '4rem', marginBottom: '16px' }}>🗺️</p>
              <p style={{ fontSize: '1.25rem', marginBottom: '8px' }}>Select a trip to view details</p>
              <p>or create a new one to start planning</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Trip Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '450px' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '20px' }}>✈️ Plan New Trip</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Trip Name *</label>
              <input
                type="text"
                value={newTrip.name}
                onChange={e => setNewTrip({ ...newTrip, name: e.target.value })}
                placeholder="e.g., Summer Vacation 2026"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Destination</label>
              <input
                type="text"
                value={newTrip.destination}
                onChange={e => setNewTrip({ ...newTrip, destination: e.target.value })}
                placeholder="e.g., Paris, France"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Start Date *</label>
                <input
                  type="date"
                  value={newTrip.startDate}
                  onChange={e => setNewTrip({ ...newTrip, startDate: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>End Date *</label>
                <input
                  type="date"
                  value={newTrip.endDate}
                  onChange={e => setNewTrip({ ...newTrip, endDate: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Notes</label>
              <textarea
                value={newTrip.notes}
                onChange={e => setNewTrip({ ...newTrip, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateModal(false)} style={{ padding: '10px 20px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleCreateTrip}
                disabled={!newTrip.name || !newTrip.startDate || !newTrip.endDate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: newTrip.name && newTrip.startDate && newTrip.endDate ? '#667eea' : '#d1d5db',
                  color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600,
                  cursor: newTrip.name && newTrip.startDate && newTrip.endDate ? 'pointer' : 'not-allowed'
                }}
              >
                Create Trip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '450px' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '20px' }}>📄 Upload Travel Document</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Document Type</label>
              <select
                value={uploadType}
                onChange={e => setUploadType(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              >
                <option value="flight_ticket">Flight Ticket</option>
                <option value="hotel_booking">Hotel Booking</option>
                <option value="itinerary">Daily Itinerary</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Select PDF File</label>
              <input
                type="file"
                accept=".pdf"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                style={{ width: '100%' }}
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '8px' }}>
                🤖 AI will automatically extract booking details and add them to your itinerary
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowUploadModal(false)} style={{ padding: '10px 20px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleUploadDocument}
                disabled={!uploadFile || uploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploadFile && !uploading ? '#667eea' : '#d1d5db',
                  color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600,
                  cursor: uploadFile && !uploading ? 'pointer' : 'not-allowed'
                }}
              >
                {uploading ? 'Processing...' : 'Upload & Extract'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '20px' }}>➕ Add Activity</h2>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Date *</label>
                <input
                  type="date"
                  value={newItem.itemDate}
                  onChange={e => setNewItem({ ...newItem, itemDate: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Time</label>
                <input
                  type="time"
                  value={newItem.itemTime}
                  onChange={e => setNewItem({ ...newItem, itemTime: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Type *</label>
              <select
                value={newItem.itemType}
                onChange={e => setNewItem({ ...newItem, itemType: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              >
                <option value="activity">🎯 Activity</option>
                <option value="flight">✈️ Flight</option>
                <option value="hotel_checkin">🏨 Hotel Check-in</option>
                <option value="hotel_checkout">🚪 Hotel Check-out</option>
                <option value="meal">🍽️ Meal</option>
                <option value="transport">🚗 Transport</option>
                <option value="meeting">👥 Meeting</option>
                <option value="other">📌 Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Title *</label>
              <input
                type="text"
                value={newItem.title}
                onChange={e => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="e.g., Visit Eiffel Tower"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Description</label>
              <textarea
                value={newItem.description}
                onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Details about this activity..."
                rows={2}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Location</label>
              <input
                type="text"
                value={newItem.location}
                onChange={e => setNewItem({ ...newItem, location: e.target.value })}
                placeholder="e.g., Paris, France"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Booking Reference</label>
              <input
                type="text"
                value={newItem.bookingReference}
                onChange={e => setNewItem({ ...newItem, bookingReference: e.target.value })}
                placeholder="e.g., ABC123"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAddItemModal(false)} style={{ padding: '10px 20px', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                disabled={!newItem.title || !newItem.itemDate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: newItem.title && newItem.itemDate ? '#667eea' : '#d1d5db',
                  color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600,
                  cursor: newItem.title && newItem.itemDate ? 'pointer' : 'not-allowed'
                }}
              >
                Add Activity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelPlanner;
